import { SpeedStreaks } from '@/ui/hud/SpeedStreaks';
import { cssColor, fmtRaceTime } from '@/ui/format';
import { el } from '@/ui/dom';

/**
 * Hud.ts — the in-race heads-up display (DOM/CSS overlay), so it's crisp and
 * easy to restyle from a design later. Shows: a progress bar to the finish, the
 * compass that points to your ball (pulsing red when it's behind you), a
 * position badge, the race timer, a speedometer, the 3-2-1-GO countdown, and a
 * city-name intro banner during the countdown.
 *
 * Results moved to the ResultsScreen (M8b): the HUD is purely live-race UI now.
 */
export interface RacingHudData {
  positionText: string; // e.g. "1st / 3"
  progress: number; // 0..1 to the finish
  speedKmh: number;
  raceTimeMs: number;
  /** Every RIVAL's ball progress (0..1) + car colour — drawn as little colour-
   *  matched soccer balls on the progress track so you can see where each
   *  opponent's ball sits along the course (M67). */
  markers: { progress: number; color: number }[];
}

export class Hud {
  private root: HTMLDivElement;
  private progressBar: HTMLDivElement;
  private progressFill: HTMLDivElement;
  /** M88 — YOUR ball, riding the end of the fill (bigger, gold-ringed). */
  private progressYou: HTMLDivElement;
  /** Pooled rival ball markers on the progress track (M67), one per rival. */
  private markerEls: HTMLDivElement[] = [];
  private markerColors: number[] = [];
  private posBadge: HTMLDivElement;
  private pauseBtn: HTMLButtonElement;
  private timer: HTMLDivElement;
  private speedVal: HTMLDivElement;
  private countdown: HTMLDivElement;
  private toastEl!: HTMLDivElement;
  private speedStreaks!: SpeedStreaks;
  private cityIntro: HTMLDivElement;
  private cityIntroName: HTMLDivElement;
  private cityIntroSub: HTMLDivElement;
  private pibeFocusEl: HTMLDivElement;
  /** Cached state so setPibeFocus is free to call every frame (see below). */
  private pibeFocusOn = false;
  private pingEl!: HTMLDivElement;
  private pingShown = -1; // last rendered bucket, so per-frame calls are ~free
  private bannerEl: HTMLDivElement | null = null;
  private spectateEl: HTMLDivElement | null = null;
  private spectateNameEl!: HTMLDivElement;

  constructor(parent: HTMLElement, playerColor: number) {
    this.root = el('div', 'hud', parent);

    // PIBE DE ORO "in the zone" golden focus overlay. Created FIRST on
    // purpose: every other HUD element is positioned with z-index:auto, so
    // later DOM order paints ABOVE it — the vignette sits over the 3D scene
    // but under every readout/button, and its backdrop blur only samples
    // what's painted behind it (the game canvas), never the HUD text.
    this.pibeFocusEl = el('div', 'hud-pibefocus', this.root);

    // Progress bar to the finish (top edge) — a glass rail since M88, with the
    // player's colour driving the fill gradient + YOUR ball marker via --pf.
    // Rival ball markers (M67) are pooled onto this bar lazily in updateRacing.
    this.progressBar = el('div', 'hud-progress', this.root);
    this.progressBar.style.setProperty('--pf', cssColor(playerColor));
    this.progressFill = el('div', 'hud-progress-fill', this.progressBar);
    // M88 (owner): YOUR ball rides the end of the fill — bigger than the rival
    // dots, gold-ringed, so your own position is unmissable at a glance.
    this.progressYou = el('div', 'hud-progress-you', this.progressBar);

    // (M67: the top-centre ball compass was removed — the on-course ground
    // pointer, BallArrow, replaces it.)

    // Pause button (top left) + race timer beside it.
    this.pauseBtn = el('button', 'hud-pause', this.root);
    this.pauseBtn.type = 'button';
    this.pauseBtn.setAttribute('aria-label', 'Pause');
    this.pauseBtn.innerHTML = '<span></span><span></span>';
    this.timer = el('div', 'hud-timer', this.root);
    this.timer.textContent = '0:00.0';

    // Position badge (top right).
    this.posBadge = el('div', 'hud-badge', this.root);
    this.posBadge.textContent = '1st / 3';

    // Speedometer (top right, under the badge — lower-right is the thumb zone).
    const speedo = el('div', 'hud-speedo', this.root);
    this.speedVal = el('div', 'hud-speedo-val', speedo);
    this.speedVal.textContent = '0';
    const unit = el('div', 'hud-speedo-unit', speedo);
    unit.textContent = 'km/h';

    // Link quality (online only): a small ping pill under the timer (M52).
    this.pingEl = el('div', 'hud-ping', this.root);
    this.pingEl.style.display = 'none';

    // Countdown (centre, hidden until set).
    this.countdown = el('div', 'hud-countdown', this.root);
    this.countdown.style.display = 'none';

    // Toast (short warnings, e.g. "TOO FAR FROM BALL!"), above the thumb zone.
    this.toastEl = el('div', 'hud-toast', this.root);

    // Boost speed streaks (M14; M145: animated — see SpeedStreaks.ts).
    this.speedStreaks = new SpeedStreaks(this.root);

    // City intro banner (lower third, shown during the countdown).
    this.cityIntro = el('div', 'hud-cityintro', this.root);
    this.cityIntro.style.display = 'none';
    this.cityIntroName = el('div', 'hud-cityintro-name', this.cityIntro);
    this.cityIntroSub = el('div', 'hud-cityintro-sub', this.cityIntro);
  }

  /** M93: re-tint the progress fill + YOU marker to the LOCAL player's ball
   *  colour — online you may be any slot (the old boot-red never updated,
   *  flagged since M45; now the ball IS the identity, so it must be right). */
  setPlayerColor(color: number): void {
    this.progressBar.style.setProperty('--pf', cssColor(color));
  }

  setCountdown(text: string | null): void {
    if (text === null) {
      this.countdown.style.display = 'none';
      return;
    }
    if (this.countdown.textContent !== text) {
      this.countdown.textContent = text;
      // Re-trigger the pop animation.
      this.countdown.style.animation = 'none';
      void this.countdown.offsetWidth;
      this.countdown.style.animation = '';
    }
    this.countdown.style.display = 'flex';
  }

  /** Show the "NEW YORK — USA" banner during the countdown (null hides it). */
  setCityIntro(name: string | null, sub = ''): void {
    if (name === null) {
      this.cityIntro.style.display = 'none';
      return;
    }
    if (this.cityIntroName.textContent !== name) {
      this.cityIntroName.textContent = name;
      this.cityIntroSub.textContent = sub;
    }
    this.cityIntro.style.display = 'flex';
  }

  updateRacing(data: RacingHudData): void {
    this.posBadge.textContent = data.positionText;
    const pct = Math.max(0, Math.min(1, data.progress)) * 100;
    this.progressFill.style.width = `${pct}%`;
    this.progressYou.style.left = `${pct}%`; // your ball marks the fill's end (M88)
    this.speedVal.textContent = String(Math.round(data.speedKmh));
    this.timer.textContent = fmtRaceTime(data.raceTimeMs);
    this.updateProgressMarkers(data.markers);
  }

  /**
   * Rival ball markers on the progress track (M67). Elements are POOLED and
   * reused across frames — the pool grows once to the field size, then every
   * frame only touches `left` (and colour when it changes), so this is ~free.
   */
  private updateProgressMarkers(markers: { progress: number; color: number }[]): void {
    while (this.markerEls.length < markers.length) {
      this.markerEls.push(el('div', 'hud-progress-marker', this.progressBar));
      this.markerColors.push(-1);
    }
    for (let i = 0; i < this.markerEls.length; i++) {
      const m = this.markerEls[i];
      if (i >= markers.length) {
        m.style.display = 'none';
        continue;
      }
      const { progress, color } = markers[i];
      m.style.display = '';
      m.style.left = `${Math.max(0, Math.min(1, progress)) * 100}%`;
      if (this.markerColors[i] !== color) {
        this.markerColors[i] = color;
        m.style.setProperty('--mk', cssColor(color));
      }
    }
  }

  /** Drive the boost speed streaks — call every race frame (M145). `on` is
   *  already false under reduced motion (the caller gates it). */
  updateSpeedLines(on: boolean, dt: number): void {
    this.speedStreaks.update(on, dt);
  }

  /**
   * PIBE DE ORO "in the zone" golden focus (owner ask): while the power runs,
   * the city outside the road ahead gets a warm golden vignette + edge blur
   * with a slow pulse — a screen-space effect, so it's one CSS class and zero
   * render cost when off. Idempotent + cheap to call every frame: the DOM is
   * only touched when the state actually flips. NOTE: the game-level
   * reducedMotion SETTING is the caller's job (gate the call in Game.ts);
   * the OS-level prefers-reduced-motion is handled in the CSS itself.
   */
  setPibeFocus(on: boolean): void {
    if (on === this.pibeFocusOn) return;
    this.pibeFocusOn = on;
    this.pibeFocusEl.classList.toggle('show', on);
  }

  /** Flash a short warning toast (e.g. "TOO FAR FROM BALL!"). */
  toast(text: string): void {
    this.toastEl.textContent = text;
    this.toastEl.classList.remove('show');
    void this.toastEl.offsetWidth; // restart the animation
    this.toastEl.classList.add('show');
  }

  /** Online link quality: "38ms" under the timer, tinted by health. null hides
   *  it (offline / not yet measured). Buckets keep per-frame calls free. */
  setPing(ms: number | null): void {
    const bucket = ms === null ? -1 : ms < 80 ? 0 : ms < 160 ? 1 : 2;
    const rounded = ms === null ? -1 : Math.round(ms / 5) * 5;
    const key = bucket * 10000 + rounded;
    if (key === this.pingShown) return;
    this.pingShown = key;
    if (ms === null) {
      this.pingEl.style.display = 'none';
      return;
    }
    this.pingEl.style.display = '';
    this.pingEl.textContent = `${rounded}ms`;
    this.pingEl.className = `hud-ping hud-ping-${bucket === 0 ? 'good' : bucket === 1 ? 'ok' : 'bad'}`;
  }

  /** A big centre banner ("FINISH!") — the beat between crossing the line and
   *  the results panel (M52). Removed by hideBanner()/setVisible(false). */
  showBanner(text: string): void {
    this.hideBanner();
    this.bannerEl = el('div', 'hud-banner', this.root);
    this.bannerEl.textContent = text;
  }

  /** Spectate banner (M74/M75): "SPECTATING · <name>" bottom-centre with ◀ ▶
   *  cycle buttons and a SKIP (→ waiting room). null removes it. Game assigns
   *  the callbacks once. */
  onSpectatePrev: (() => void) | null = null;
  onSpectateNext: (() => void) | null = null;
  onSpectateSkip: (() => void) | null = null;
  setSpectate(name: string | null): void {
    if (name === null) {
      this.spectateEl?.remove();
      this.spectateEl = null;
      return;
    }
    if (!this.spectateEl) {
      this.spectateEl = el('div', 'hud-spectate', this.root);
      const prev = el('button', 'hud-spectate-btn', this.spectateEl);
      prev.type = 'button';
      prev.setAttribute('aria-label', 'Watch previous racer');
      prev.textContent = '‹';
      prev.addEventListener('click', () => this.onSpectatePrev?.());
      const mid = el('div', 'hud-spectate-mid', this.spectateEl);
      el('div', 'hud-spectate-kicker', mid).textContent = 'SPECTATING';
      this.spectateNameEl = el('div', 'hud-spectate-name', mid);
      const next = el('button', 'hud-spectate-btn', this.spectateEl);
      next.type = 'button';
      next.setAttribute('aria-label', 'Watch next racer');
      next.textContent = '›';
      next.addEventListener('click', () => this.onSpectateNext?.());
      const skip = el('button', 'hud-spectate-skip', this.spectateEl);
      skip.type = 'button';
      skip.setAttribute('aria-label', 'Skip spectating and wait');
      skip.textContent = 'SKIP';
      skip.addEventListener('click', () => this.onSpectateSkip?.());
    }
    this.spectateNameEl.textContent = name;
  }

  hideBanner(): void {
    this.bannerEl?.remove();
    this.bannerEl = null;
  }

  /** Wire the touch pause button (fires the same path as Escape / Start). */
  onPause(cb: () => void): void {
    this.pauseBtn.addEventListener('click', cb);
  }

  /** The whole HUD layer on/off (menus hide it). */
  setVisible(visible: boolean): void {
    // Leaving the racing HUD (results, menus) always drops the golden focus —
    // renderRace stops being called, so nobody else would turn it off (M51).
    if (!visible) {
      this.speedStreaks.reset(); // M145: never leave a boost streaking over a menu
      this.setPibeFocus(false);
      this.hideBanner();
      this.setSpectate(null);
    }
    this.root.style.display = visible ? '' : 'none';
  }

  dispose(): void {
    this.root.remove();
  }
}
